import { useTranslation } from 'react-i18next';
import { Sparkles, Shield, Zap, Bell, KeyRound, Network } from 'lucide-react';
import { useTelegramPixel } from '@/hooks/useTelegramPixel';

const AppStorePromo = () => {
  const { t } = useTranslation();
  const { trackAppDownloadClick } = useTelegramPixel();

  return (
    <div className="relative mb-6 sm:mb-8">
      {/* Background glow effects - hidden on mobile for performance */}
      <div className="absolute inset-0 -z-10 overflow-hidden hidden sm:block">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[400px] h-[400px] bg-primary/20 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[300px] h-[300px] bg-cyan-500/15 rounded-full blur-[80px] animate-[pulse_4s_ease-in-out_infinite_0.5s]" />
      </div>

      {/* Main container */}
      <div className="relative p-4 sm:p-8 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-background/90 via-muted/50 to-background/90 sm:backdrop-blur-xl border border-border/40 overflow-hidden">
        {/* Top shine */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />

        <div className="relative flex flex-col lg:flex-row items-center gap-6 lg:gap-12">
          
          {/* Phone Mockup */}
          <div className="relative flex-shrink-0 order-1 lg:order-2">
            {/* Outer glow ring */}
            <div className="absolute inset-0 -m-4 bg-gradient-to-br from-primary/30 via-cyan-500/20 to-primary/30 rounded-[3rem] blur-2xl animate-pulse hidden sm:block" />
            
            {/* Phone frame */}
            <div className="relative w-[180px] sm:w-[220px] h-[360px] sm:h-[440px] bg-gradient-to-b from-zinc-800 to-zinc-900 rounded-[2.5rem] sm:rounded-[3rem] p-1.5 sm:p-2 shadow-2xl shadow-black/50">
              {/* Inner bezel */}
              <div className="absolute inset-1.5 sm:inset-2 rounded-[2rem] sm:rounded-[2.5rem] bg-gradient-to-b from-zinc-700 to-zinc-800" />
              
              {/* Screen */}
              <div className="relative h-full w-full rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden bg-background">
                {/* Dynamic Island / Notch */}
                <div className="absolute top-2 sm:top-3 left-1/2 -translate-x-1/2 w-20 sm:w-24 h-6 sm:h-7 bg-black rounded-full z-20" />
                
                {/* App Screen Content */}
                <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted/30">
                  {/* Status bar simulation */}
                  <div className="flex justify-between items-center px-5 pt-2 text-[8px] sm:text-[10px] text-muted-foreground">
                    <span>9:41</span>
                    <div className="flex gap-1">
                      <div className="w-3 h-1.5 sm:w-4 sm:h-2 bg-muted-foreground/50 rounded-sm" />
                      <div className="w-3 h-1.5 sm:w-4 sm:h-2 bg-muted-foreground/50 rounded-sm" />
                    </div>
                  </div>
                  
                  {/* App header */}
                  <div className="mt-8 sm:mt-10 px-3 sm:px-4">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                      </div>
                      <span className="text-[10px] sm:text-xs font-semibold">Tron<span className="text-primary">scope</span></span>
                    </div>
                    
                    {/* Balance card */}
                    <div className="p-2.5 sm:p-3 rounded-xl bg-gradient-to-br from-primary/10 to-cyan-500/10 border border-primary/20 mb-3 sm:mb-4">
                      <p className="text-[8px] sm:text-[10px] text-muted-foreground mb-0.5 sm:mb-1">{t('wallet.totalBalance', 'Total Balance')}</p>
                      <p className="text-sm sm:text-lg font-bold">$12,458.32</p>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-[8px] sm:text-[10px] text-success">+2.4%</span>
                        <span className="text-[8px] sm:text-[10px] text-muted-foreground">24h</span>
                      </div>
                    </div>
                    
                    {/* Quick actions */}
                    <div className="grid grid-cols-4 gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                      {['Send', 'Receive', 'Stake', 'Swap'].map((action) => (
                        <div key={action} className="flex flex-col items-center gap-0.5 sm:gap-1">
                          <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-muted/50 flex items-center justify-center">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-primary/30" />
                          </div>
                          <span className="text-[7px] sm:text-[8px] text-muted-foreground">{action}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Assets preview */}
                    <div className="space-y-1.5 sm:space-y-2">
                      <div className="flex items-center justify-between p-1.5 sm:p-2 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <div className="w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-red-500/20 flex items-center justify-center text-[8px] sm:text-[10px] font-bold text-red-500">T</div>
                          <div>
                            <p className="text-[8px] sm:text-[10px] font-medium">TRX</p>
                            <p className="text-[7px] sm:text-[8px] text-muted-foreground">45,230</p>
                          </div>
                        </div>
                        <span className="text-[8px] sm:text-[10px] font-medium">$10,458</span>
                      </div>
                      <div className="flex items-center justify-between p-1.5 sm:p-2 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <div className="w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-green-500/20 flex items-center justify-center text-[8px] sm:text-[10px] font-bold text-green-500">$</div>
                          <div>
                            <p className="text-[8px] sm:text-[10px] font-medium">USDT</p>
                            <p className="text-[7px] sm:text-[8px] text-muted-foreground">2,000</p>
                          </div>
                        </div>
                        <span className="text-[8px] sm:text-[10px] font-medium">$2,000</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Screen reflection */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none" />
              </div>
              
              {/* Side buttons */}
              <div className="absolute left-0 top-20 sm:top-24 w-0.5 h-6 sm:h-8 bg-zinc-700 rounded-l" />
              <div className="absolute left-0 top-32 sm:top-36 w-0.5 h-10 sm:h-12 bg-zinc-700 rounded-l" />
              <div className="absolute left-0 top-44 sm:top-52 w-0.5 h-10 sm:h-12 bg-zinc-700 rounded-l" />
              <div className="absolute right-0 top-28 sm:top-32 w-0.5 h-12 sm:h-14 bg-zinc-700 rounded-r" />
            </div>
            
            {/* Floating elements */}
            <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-10 h-10 sm:w-14 sm:h-14 rounded-2xl bg-primary/20 sm:backdrop-blur-sm border border-primary/30 flex items-center justify-center animate-bounce shadow-lg shadow-primary/20">
              <Shield className="w-5 h-5 sm:w-7 sm:h-7 text-primary" />
            </div>
            <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-success/20 sm:backdrop-blur-sm border border-success/30 flex items-center justify-center animate-[bounce_1s_ease-in-out_infinite_0.2s] shadow-lg shadow-success/20">
              <Zap className="w-4 h-4 sm:w-6 sm:h-6 text-success" />
            </div>
          </div>

          {/* Text Content */}
          <div className="flex-1 text-center lg:text-left order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-4">
              <Bell className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-medium text-primary">{t('hero.appAvailable', 'iOS App Available')}</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
              <span className="text-foreground">{t('hero.appTitle', 'Your TRON Wallet')}</span>
              <br />
              <span className="bg-gradient-to-r from-primary via-cyan-400 to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                {t('hero.appSubtitle', 'In Your Pocket')}
              </span>
            </h2>
            
            <p className="text-sm sm:text-base text-muted-foreground mb-6 max-w-md mx-auto lg:mx-0">
              {t('hero.appDescription', 'Secure wallet, staking with up to 24% APY, real-time notifications and full blockchain explorer — all in one app.')}
            </p>
            
            {/* Features list */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-3 mb-6">
              {[
                { icon: KeyRound, text: t('hero.walletFeature1', 'Non-Custodial'), color: 'text-green-500' },
                { icon: Network, text: t('hero.walletFeature2', 'Decentralized'), color: 'text-cyan-500' },
                { icon: Shield, text: t('hero.walletFeature3', 'Secure'), color: 'text-primary' },
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted/50 border border-border/50">
                  <feature.icon className={`w-3.5 h-3.5 ${feature.color}`} />
                  <span className="text-xs font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
            
            {/* App Store Button */}
            <a 
              href="https://apps.apple.com/app/tronscope/id6756782437"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackAppDownloadClick({ buttonLocation: 'hero_promo', pageMode: 'wallet' })}
              className="group relative inline-flex items-center gap-3 px-5 sm:px-6 py-3 sm:py-4 rounded-2xl bg-gradient-to-r from-zinc-900 to-zinc-800 dark:from-zinc-800 dark:to-zinc-700 text-white border border-zinc-700/50 hover:border-zinc-500 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:-translate-y-0.5"
            >
              {/* Apple Logo */}
              <svg className="w-7 h-7 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              
              <div className="flex flex-col items-start leading-tight">
                <span className="text-[11px] sm:text-xs text-zinc-400 group-hover:text-zinc-300 transition-colors">
                  {t('hero.downloadOn', 'Download on the')}
                </span>
                <span className="text-lg sm:text-xl font-semibold -mt-0.5">
                  App Store
                </span>
              </div>
              
              {/* Subtle glow on hover */}
              <div className="absolute inset-0 rounded-2xl bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              
              {/* Animated border glow */}
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-primary/0 via-primary/50 to-primary/0 opacity-0 group-hover:opacity-100 blur-sm transition-opacity -z-10" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppStorePromo;
